import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import type { Agency } from "@asap/shared/schemas/agency";
import type { BusinessLifecycleStage } from "@asap/shared/types/supabase.types.gen";
import { agencyColumns } from "@asap/shared/utils/supabase.columns";
import type { SortingState } from "@tanstack/vue-table";

type AgencyFilters = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  companyId?: string;
  status?: BusinessLifecycleStage;
  orders: { column: string; direction: "asc" | "desc" }[];
};

type AgencyState = {
  selectedFilters: AgencyFilters;
  loading: boolean;
  totalItems: number;
  showCreateModal: boolean;
  sorting: SortingState;
};

const agencyState = reactive<AgencyState>({
  selectedFilters: {
    pageSize: 20,
    pageIndex: 0,
    orders: [],
    search: undefined,
    companyId: undefined,
    status: undefined,
  },
  totalItems: 0,
  showCreateModal: false,
  loading: false,
  sorting: [{ id: "created_at", desc: true }],
});

export const useAgencies = function () {
  const { invoke } = useApi();

  const convertSortingToOrders = (sorting: SortingState): { column: string; direction: "asc" | "desc" }[] => {
    return sorting.map((sort) => ({
      column: sort.id,
      direction: sort.desc ? "desc" : "asc",
    }));
  };

  const selectMultipleForTable = async function (filters: AgencyFilters): Promise<{ data: Agency[]; count: number }> {
    agencyState.loading = true;
    const order = filters.orders.at(0);

    const response = await invoke({
      path: "agencies",
      method: "GET",
      query: {
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        orderColumn: order?.column,
        orderDirection: order?.direction,
        companyId: filters.companyId,
        status: filters.status,
        contractIsAmendment: false,
      },
    });

    agencyState.loading = false;

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return { data: response.data.items, count: response.data.totalItems };
  };

  return {
    agencyState,
    selectMultipleForTable,
    convertSortingToOrders,
  };
};

export const useAgency = () => {
  const supabase = useSupabase();

  const selectSingle = async (id: string) => {
    const { data, error } = await supabase.from("agency").select(agencyColumns).eq("id", id).single();

    if (error) throw new Error(error.message);

    return { data };
  };

  return {
    selectSingle,
  };
};
